import { Component, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild, ViewChildren, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, lastValueFrom } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import Konva from 'konva';
import { tinykeys } from 'tinykeys';
import moment from 'moment';

// models
import {
	EquipmentsDownloadModalComponent,
	IInputEquipmentsModalData,
	STAND_COMPLETION_ADDITIONAL_PARAMS
} from '../../../core/modals/equipments-download-modal/equipments-download-modal.component';
import { DwUserActivityNames } from '../../../core/models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../../core/models/dwUsersActivityTypes';
import { Block } from '../../../planogram/models/block';
import { CanvasFlipMode } from '../../../core/models/canvasMode';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../../../planogram/models/equipment-base';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { Figure } from '../../../equipment/models/figures/figure';
import { IChatUser } from '../../../core/services/layout.service';
import { IConfirmModalAnswer } from '../../../core/modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../../core/modals/confirm-modal/models/confirmModalData';
import { IPlanogram } from '../../../planogram/models/planogram';
import { IStandConversationImage, IStandConversationMessage, IStandConversationDone } from '../../../core/services/planograms.service';
import { IStandEquipmentRequest } from '../../../core/models/standEquipmentRequest';
import { ITimeTracker, ITimeTrackingRecord, ITimeTrackingStatus, ITimeTrackingDelete } from '../../../core/services/time-tracking.service';
import { konvaSettings } from '../../../equipment/models/konvaSettings';
import { MixpanelEvents } from '../../../core/models/mixpanelEvents';
import { NORMALIZED_ELEMENTS } from '../../../planogram/services/planogram-activity.service';
import { PlanogramEditComponent } from '../../../planogram/planogram-edit/planogram-edit.component';
import { PlanogramViewBehavior } from '../../../planogram/models/planogramViewBehavior';
import { PlanogramViewBehaviorFactory } from '../../../planogram/models/planogramViewBehaviorFactory';
import { Product } from '../../../planogram/models/product';
import { ProductGroup } from '../../../planogram/models/product-group';
import { SocketGroup } from '../../../core/services/socket.service';

// components
import { ConfirmModalComponent } from '../../../core/modals/confirm-modal/confirm-modal.component';
import { EquipmentModelComponent } from '../../../core/components/equipment-model/equipment-model.component';
import { PhotoReportModalComponent } from '../../../core/modals/photo-report-modal/photo-report-modal.component';
import { PlanogramComponent } from '../../../planogram/components/planogram/planogram.component';

// services
import { CanvasDrawerService } from '../../../core/services/canvas-drawer.service';
import { CanvasDrawerDetailedService } from '../../../core/services/canvas-drawer-detailed.service';
import { ChatService } from '../../../core/services/chat.service';
import { EquipmentService } from '../../../core/services/equipment.service';
import { ExcelService } from '../../../core/services/xls.service';
import { LayoutService } from '../../../core/services/layout.service';
import { MixpanelService } from '../../../core/services/mixpanel.service';
import { PlanogramLayoutService } from '../../../planogram/services/planogram-layout.service';
import { PlanogramsService } from '../../../core/services/planograms.service';
import { IPlanInfo, PlansService } from '../../../core/services/plans.service';
import { PlanogramCompletionService } from '../../../core/services/planogram-completion.service';
import { SessionService } from '../../../core/services/session.service';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { SocketService } from '../../../core/services/socket.service';
import { TimeTrackingService } from '../../../core/services/time-tracking.service';
import { Utils } from '../../../core/services/utils.service';
import { DwUsersActivityService } from '../../../core/services/dw-users-activity.service';
import { DateFormatService } from '../../../core/services/date-format.service';
import { IPlanogramBounds } from '../../../planogram/planogram-edit/models/planogramBounds';
import { Point } from '../../../core/models/point';

const canvasOffset: number = konvaSettings.canvasOffset,
	defaultTimeTracker: ITimeTrackingStatus = { enable: false, key: '' },
	BACK_WALLS_LIST: Array<EquipmentElements> = [
		EquipmentElements.BackWall,
		EquipmentElements.CentralPillar,
		EquipmentElements.HookWall,
		EquipmentElements.PerforatedWall,
		EquipmentElements.Showcase
	];

interface Screen {
	id: number;
	planogram?: number;
	name?: string;
	loading: boolean;
	standData: any;
	stand?: EquipmentBase;
	filters?: any;
	active: boolean;
}

export interface StandExecution {
	created_at: string;
	deadline_date: string;
	enable_time_tracking: boolean;
	form_user_full_name: string;
	id: number;
	is_overdue: boolean;
	time_tracking_record: ITimeTrackingRecord;
	user_full_name: string;
}

@Component({
    selector: 'app-execution-stand',
    templateUrl: './execution-stand.component.html',
    styleUrls: ['./execution-stand.component.scss'],
    providers: [PlanogramCompletionService, CanvasDrawerService, CanvasDrawerDetailedService, ChatService],
    standalone: false
})
export class ExecutionStandComponent implements OnInit, OnDestroy {
	@ViewChild(PlanogramComponent) standView: PlanogramComponent;
	@ViewChild('canvasContainer') canvasContainer: ElementRef;
	@ViewChild('newCanvasContainer') newCanvasContainer: ElementRef;
	@ViewChild('currentVersionCanvas') currentVersionCanvas: ElementRef;
	@ViewChild('newVersionCanvas') newVersionCanvas: ElementRef;

	currentVersionStage: Konva.Stage;
	newVersionStage: Konva.Stage;
	activeStand: any;
	current_ids: number[] = [];
	socketSubscriptions: Array<Subscription> = [];

	equipmentStages: any = [];

	standName: string = 'Stand name';
	planName: string = 'Planogram name';
	planId: number = -1;
	storeId: number = null;
	standId: number = -1;
	selectedStand: any = -1;
	selectedPlanogram: any = -1;
	stand_ids: string = '';
	products: Product[] = [];
	blocks: Block[];
	plansFilter = [];
	standFilters = [];

	planogramLayer: Konva.Layer = new Konva.Layer();
	detalisationLayer: Konva.Layer = new Konva.Layer();
	sizesLayer: Konva.Layer = new Konva.Layer();
	detalisationSizesLayer: Konva.Layer = new Konva.Layer();
	tooltipLayer: Konva.Layer = new Konva.Layer();
	boxLayer: Konva.Layer = new Konva.Layer();

	newPlanogramLayer: Konva.Layer = new Konva.Layer();
	newDetalisationLayer: Konva.Layer = new Konva.Layer();
	newSizesLayer: Konva.Layer = new Konva.Layer();
	newDetalisationSizesLayer: Konva.Layer = new Konva.Layer();
	newTooltipLayer: Konva.Layer = new Konva.Layer();
	newBoxLayer: Konva.Layer = new Konva.Layer();

	labelsDict = {
		PROMO_LABEL: 'А',
		NEW_LABEL: 'Н'
	};

	showLoader: boolean = false;

	zoom: number = 100; // масштаб;
	zoomTargets: Array<HTMLElement> = [];
	
	reportParams = {
		turnover: 0.5,
		qty: 0.2,
		receipts_qty: 0.2,
		profit: 0.1
	};

	stands: Array<IPlanogram> = [];
	standData: any = {};
	table: any = {};
	hasEquipmentBoxes: boolean = false;
	hasEquipmentBarriers: boolean = false;
	hasPOSMaterials: boolean = false;
	model3DMode: boolean = false;

	generateLink = {
		column: 'product_name',
		func(item) {
			return {
				link: true,
				route: `/product/view/${item.id}`,
				name: item.name
			};
		}
	};

	tableDataExists: boolean = false;

	group: number;

	rightPanelState: 'active' | 'inactive' = 'inactive';

	equipmentMode: boolean = false;

	loaded: boolean = false;
	complectionView: string = 'execution';

	analyticsList: boolean = false;
	sizesMode: boolean = false;
	articleMode: boolean = false;
	flipMode: CanvasFlipMode;
	sequenceNumberMode: boolean = false;
	transparentBoxesMode: boolean = false;
	barriersMode: boolean = true;
	POSMaterialsMode: boolean = true;
	canEdit: boolean = false;
	screens: Screen[] = [];
	scrollToMessage: number = null;
	standsList: any;
	next_messages: number = 1;
	chatMessages: Array<IStandConversationMessage> = [];
	chatUsers: Array<IChatUser> = [];
	errorStands: any = {};
	layoutOpen: boolean = false;
	tinyKeysSubscription: any;
	deadline: { date: string, overdue: boolean } = { date: null, overdue: false };
	timeTracking: ITimeTrackingStatus = defaultTimeTracker;
	timer: ITimeTracker;
    isExporting: WritableSignal<boolean> = signal<boolean>(false);

	@ViewChildren('equipment3DModel') equipment3DModels: Array<EquipmentModelComponent>;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private translate: TranslateService,
		private drawer: CanvasDrawerService,
		private detailedDrawer: CanvasDrawerDetailedService,
		private equipmentService: EquipmentService,
		private excelService: ExcelService,
		private layoutService: LayoutService,
		private mixPanelService: MixpanelService,
		private planogramLayoutService: PlanogramLayoutService,
		public planogramCompletionService: PlanogramCompletionService,
		private planogramsService: PlanogramsService,
		private plansService: PlansService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private socketService: SocketService,
		private timeTrackingService: TimeTrackingService,
		private dwUsersActivityService: DwUsersActivityService,
		private dateFormatService: DateFormatService
	) {
		this.scrollToMessage = this.route.snapshot.queryParams.message ?? null;
		this.articleMode = !this.sessionService.personalSettings.planogramImagesMode;
		this.sequenceNumberMode = this.sessionService.personalSettings.sequenceNumberMode;
		this.transparentBoxesMode = this.sessionService.personalSettings.transparentBoxesMode;
		this.tinyKeysSubscription = tinykeys(window, {
			'$mod+KeyS': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.export();
			},
			'PageDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				if (!this.stands[0]?.detalisation && !this.stands[1]?.detalisation) return;

				event.preventDefault();

				this.stands.forEach((planogram: IPlanogram) => {
					this.changeDetailedElement(planogram, planogram?.detailedElementPrev);
				});
			},
			'PageUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				if (!this.stands[0]?.detalisation && !this.stands[1]?.detalisation) return;

				event.preventDefault();

				this.stands.forEach((planogram: IPlanogram) => {
					this.changeDetailedElement(planogram, planogram?.detailedElementNext);
				});
			},
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();

				this.stands.forEach((planogram:IPlanogram) => this.closeDetailedView(planogram));
			}
		});
		this.flipMode = new CanvasFlipMode(false, false);
	}

	openDetailedView(planogram: IPlanogram): void {
		if (!planogram.detaledElement) return;
		planogram.detalisation = true;
		this.planogramLayoutService.setDetailedVariables(planogram, planogram.detaledElement);
		this.canvasRender(planogram);
	}

	closeDetailedView(planogram: IPlanogram): void {
		planogram.detalisation = false;
		planogram.detaledElement = null;

		planogram.stand.allElements.forEach((el: Element) => {
			el.shapes.forEach((shape: Figure) => (shape as any).representation = 'front');
		});

		this.canvasRender(planogram);
	}

	changeDetailedElement(planogram: IPlanogram, detailedElement: Element): void {
		if (!detailedElement) return;
		planogram.detaledElement = detailedElement;
		this.planogramLayoutService.setDetailedVariables(planogram, planogram.detaledElement);
		this.canvasRender();
	}

	async getStand(standId: number, current) {
		let standInfo = await lastValueFrom(
			this.planogramsService.getItem$(standId, {
				planogram_id: this.planId,
				only_laid_products: true
			})
		);

		if (!standInfo) return;

		if (standId == this.route.snapshot.params.id) {
			this.standId = standId;
			this.standName = standInfo.name;
			if (!this.planId && standInfo.planogram_stand.length) {
				this.planId = standInfo.planogram_stand[0].planogram;
				standInfo = await lastValueFrom(
					this.planogramsService.getItem$(standId, {
						planogram_id: this.planId,
						only_laid_products: true
					})
				);
				if (!standInfo) return;
			}
		}
		if (current != 0) {
			this.deadline = {
				date: standInfo.execution.deadline_date,
				overdue: standInfo.execution.is_overdue && Utils.dateDayComparison(new Date(), new Date(standInfo.execution.deadline_date))
			};

			if (standInfo.show_execution_button) await this.setTimeTracker(standInfo.execution, standInfo.name);
		}

		const response: any = {
			visible: true,
			layerObjects: {
				planogramGroup: new Konva.Group(),
				detalisationGroup: new Konva.Group(),
				sizesGroup: new Konva.Group(),
				tooltipGroup: new Konva.Group(),
				boxGroup: new Konva.Group()
			},
			id: standInfo.id,
			name: standInfo.name,
			show_execution_button: standInfo.show_execution_button,
			main_stand: standInfo.main_stand,
			blocks: standInfo.blocks,
			chat: standInfo.chat
		};

		if (current == 0) {
			this.planogramLayer.add(response.layerObjects.planogramGroup);
			this.detalisationLayer.add(response.layerObjects.detalisationGroup);
			this.sizesLayer.add(response.layerObjects.sizesGroup);
			this.tooltipLayer.add(response.layerObjects.tooltipGroup);
			this.boxLayer.add(response.layerObjects.boxGroup);
		} else {
			this.newPlanogramLayer.add(response.layerObjects.planogramGroup);
			this.newDetalisationLayer.add(response.layerObjects.detalisationGroup);
			this.newSizesLayer.add(response.layerObjects.sizesGroup);
			this.newTooltipLayer.add(response.layerObjects.tooltipGroup);
			this.newBoxLayer.add(response.layerObjects.boxGroup);
		}

		response.stand = new EquipmentBase({
			id: standInfo.instrument.id,
			name: standInfo.instrument.name,
			code: standInfo.instrument.code,
			producer: standInfo.instrument.producer,
			alignment: standInfo.alignment,
			arbitrary: standInfo.arbitrary,
			posMaterials: standInfo.pos_materials,
			textMaterials: standInfo.text_materials
		});

		this.equipmentService.setEquipmentConfElements(response.stand, standInfo.instrument.conf.elements);

		if (standInfo.conf.products) {
			let products = standInfo.conf.products.map((p) => new Product(p));

			const ids = standInfo.conf.products.map((p) => p.id);
			products.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
			this.products = this.products.concat(
				products.filter((pr) => !this.products.map((i) => i.id).includes(pr.id))
			);
		}

		if (standInfo.conf.reportParams) {
			this.reportParams = standInfo.conf.reportParams;
		}

		response.stand.products = [];

		standInfo.products
			.sort((a, b) => a.position_y - b.position_y)
			.forEach((p) => {
				const product = this.products.find((product) => product.id == p.master_product);

				if (!product) return;

				const element = response.stand.allElements.find((element) => element.id === p.element_id);

				if (!element) return;

				const begin = {
						x: p.position_x,
						y: p.position_y,
						z: p.position_z
					},
					{ qty, rotation, angle, packing, weight } = p,
					placementHeight = p.placement_height,
					productGroup = new ProductGroup({
						product,
						element,
						begin,
						qty,
						weight,
						rotation,
						angle,
						placementHeight,
						selectPacking: packing,
						block: p.block,
						height_compression_coef: p.height_compression_coef,
						width_compression_coef: p.width_compression_coef,
						depth_compression_coef: p.depth_compression_coef,
						applied_compressions: p.applied_compressions,
						dependProduct: Utils.getDependProduct(p, response.stand.products, element),
						detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
					});

				productGroup.placementDepth = p.placement_depth;

				if (!productGroup.element) return;

				response.stand.products.push(productGroup);
			});

		if (standInfo.blocks) response.stand.setBlocks(standInfo.blocks);

		response.stand.orderedElements = Utils.setElementsOrder(response.stand.allElements);

		response.stand.orderedElements.forEach((el) => {
			if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) el.normalize(response.stand);
		});

		return response;
	}

	async ngOnInit() {
		this.showLoader = true;
		let stands: number[] = this.route.snapshot.params.id.split(',').map(Number);

		this.current_ids = stands;

		this.planId = this.route.snapshot.queryParams.planogram;
		this.stands = await Promise.all(
			stands.map((s, index) => this.getStand(s, index))
		);
		if (!this.stands[0] || !this.stands[1]) return;
		this.flipMode.check(this.stands[0].stand);
		this.hasEquipmentBoxes = this.stands[0].stand.hasBoxes;
		this.hasEquipmentBarriers = this.stands[0].stand.hasBarriers;
		this.hasPOSMaterials = this.stands.some((stand: IPlanogram) => stand.stand.POSMaterials.length);
		this.standsList = await this.plansService.getLibraryLastVersion(
			this.planId
		);
		if (this.planId) {
			const planogram = await lastValueFrom(this.plansService.getItem$(this.planId));
			if (!planogram) return;
			this.planName = planogram.name;
			this.storeId = planogram.shops?.[0];
		}

		let allStandsList = await this.planogramsService.getLibrary().toPromise(),
			selectStand = allStandsList.filter(
				(i) => i.main_stand == this.stands[1].main_stand
			),
			planogramsIdList = selectStand[0].planograms.map((i) => i.id),
			planogramsList = await Promise.all(
				planogramsIdList.map((s) => lastValueFrom(this.plansService.getItem$(s)))
			),
			planogramIndex = planogramsList.findIndex(
				(item) => (item as any).id == this.route.snapshot.queryParams.planogram
			),
			standIndex = this.standsList.findIndex(
				(item) => item.main_stand == this.stands[1].main_stand
			);

		if (!planogramsList || planogramsList.some((plan: IPlanInfo) => !plan)) return;

		this.plansFilter =
			planogramIndex != -1
				? this.changeIndex(planogramsList, planogramIndex, 0)
				: planogramsList;
		this.standFilters =
			standIndex != -1
				? this.changeIndex(this.standsList, standIndex, 0)
				: this.standsList;

		const [users, messages] = await Promise.all([
			this.layoutService.getUsers(),
			this.loadMessages()
		]);

		this.chatUsers = users;
		this.chatMessages = messages;

		this.calculateDifference();
		this.zoomApply(100);

		const canvasTopPoint =
				this.currentVersionStage.getAttr('height') / this.stands[0].scale,
			standsProducts = Utils.to1DArray(
				this.stands.map((s) => s.stand.products)
			);

		if (
			standsProducts.some(
				(pg) => canvasTopPoint - pg.begin.y - pg.product.height < 0
			)
		) {
			const sortedProducts = standsProducts.sort(
				(a, b) => b.begin.y - a.begin.y && b.product.height - a.product.height
			);
			if (!sortedProducts[0].isLooseForm)
				this.calculateZoom(sortedProducts[0]);
		}

		this.stand_ids = this.stands.map((i) => i.id).join(',');
		this.standId = stands[1];
		this.standName = this.stands[1].name;
		this.selectedStand = stands[1];

		this.showLoader = false;

		if (this.timeTracking.enable && !this.timer.time.asMilliseconds()) {
			this.openTimeTrackerModal().subscribe((value: boolean) => {
				if (value) this.timeTrackingService.startTracking(this.timeTracking.key);
			});
		}

		this.translate.onLangChange.subscribe(async () => {
			this.stands = await Promise.all(
				stands.map((s, index) => this.getStand(s, index))
			);
			this.zoomApply(this.zoom);
		});

		this.socketService.addSubscription(SocketGroup.StandConversation, { stand_id: Number(this.stands[1].main_stand) });

		this.socketSubscriptions.push(
			this.socketService.conversationReadMessage.subscribe((data: IStandConversationDone) => {
				this.chatMessages.forEach((message: IStandConversationMessage) => {
					if (message.id == data.id) message.done = data.done;
				});
				this.chatMessages = [...this.chatMessages];
			}),

			this.socketService.conversationUpdateImage.subscribe((data: IStandConversationImage) => {
				this.chatMessages.forEach((message: IStandConversationMessage) => {
					if (message.id == data.stand_conversation_id) {
						message.files = data.files;
					}
				});
				this.chatMessages = [...this.chatMessages];
			}),

			this.socketService.conversationNewMessage.subscribe((data: IStandConversationMessage) => {
				this.chatMessages = [...this.chatMessages, data];
			}),

			this.socketService.timeTrackerDelete.subscribe((tracker: ITimeTrackingDelete) => {
				if (tracker.execution_id == this.timeTracking.key) this.stands[1].show_execution_button = false;
			}),

			this.socketService.timeTrackingDeleteAll.subscribe(() => {
				this.stands[1].show_execution_button = false;
			})
		);
	}

	async loadMessages() {
		if (!this.next_messages) return;

		const response = await this.layoutService.getMessages(
			this.current_ids[1],
			this.next_messages
		);

		this.next_messages = response.next
			? (Number(PlanogramEditComponent.getNextPage(response.next)) || null)
			: null;

		return response.results;
	}

	async setTimeTracker(execution: StandExecution, standName: string): Promise<void> {
		this.timeTracking.enable = execution.enable_time_tracking;
		if (!this.timeTracking.enable) return;
		const tracking: ITimeTrackingRecord = execution.time_tracking_record;
		this.timeTracking.key = execution.id;
		this.timeTracking.enable = true;
		if (!tracking || (!tracking.is_running && !tracking.time)) {
			this.timeTrackingService.setTimer(this.timeTracking.key, moment.duration(), standName, this.router.url);
			this.timer = this.timeTrackingService.timers.get(this.timeTracking.key);
		} else {
			await this.timeTrackingService.initTrackersPromise;
			this.timer = this.timeTrackingService.timers.get(this.timeTracking.key);
		}
	}

	openTimeTrackerModal(): Observable<boolean> {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'DO_EXECUTE_WITH_TRACKING',
				secondLabel: 'WANT_START_EXECUTING',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		return dialogRef
			.afterClosed()
			.pipe(map((result: IConfirmModalAnswer) => result.answer));
	}

	calculateDifference() {
		const [from, to] = this.stands;

		this.standData[to.id] = {};
		this.standData[from.id] = {};
		this.errorStands[to.id] = {};
		this.errorStands[from.id] = {};

		to.stand.products.forEach((pg) => {
			const from_pg = from.stand.products.find(
				(f_pg) =>
					f_pg.product.id == pg.product.id &&
					Utils.equalObjects(f_pg.begin, pg.begin, true)
			);

			if (!from_pg || !Utils.isEqualProductGroups(pg, from_pg)) {
				this.standData[to.id][pg.getIdHash()] = { color: 'red' };
				this.errorStands[to.id][pg.getIdHash()] = pg;
			}
		});
		from.stand.products.forEach((pg) => {
			const to_pg = to.stand.products.find(
				(f_pg) =>
					f_pg.product.id == pg.product.id &&
					Utils.equalObjects(f_pg.begin, pg.begin, true)
			);

			if (!to_pg || !Utils.isEqualProductGroups(pg, to_pg)) {
				this.standData[from.id][pg.getIdHash()] = { color: 'red' };
				this.errorStands[from.id][pg.getIdHash()] = pg;
			}
		});
	}

	showPhotoreportModal() {
		if (this.timeTracking.enable && !this.timer.time.asMilliseconds()) return;
		const dialogRef = this.dialog.open(PhotoReportModalComponent, {
			width: '500px',
			maxHeight: '90vh',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				planogram: this.planId,
				trackerWarning: this.timeTracking.enable
			}
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result) return;

			if (this.timeTracking.enable) {
				const timer: ITimeTracker = this.timeTrackingService.timers.get(this.timeTracking.key);
				if (timer) timer.running = false;
			}
			try {
				const res = await this.planogramsService.executionDone(
					result,
					this.current_ids[1]
				);

				if (res === 'Ok') {
					this.stands[1].show_execution_button = false;
					if (this.timeTracking.enable) {
						this.mixPanelService.trackPage(MixpanelEvents.PlanogramExecutionWithTimeTracker);
						this.timeTrackingService.deleteTracker(this.timeTracking.key);
					}
					this.snackBarService.setSnackBarNotification({
						messageText: 'ATTACHED_PR'
					});
				} else if (res.status === 400) {
					if (this.timeTracking.enable) this.timeTrackingService.deleteTracker(this.timeTracking.key);
					this.snackBarService.setSnackBarNotification({
						messageText: res.error
					});
					this.stands[1].show_execution_button = false;
				} else if (JSON.parse(res._body).msg) {
					this.snackBarService.setSnackBarNotification({
						messageText: JSON.parse(res._body).msg
					});
					this.stands[1].show_execution_button = false;
				}
			} catch (err) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'EXECUTION_DONE_ERROR_MESSAGE'
				});
			}
		});
	}

	toggleComplectionView(v) {
		this.complectionView = v;
	}

	toggle3dModel(): void {
		this.model3DMode = !this.model3DMode;
		if (!this.model3DMode) this.setCanvasContainers();
	}

	setCanvasContainers(): void {
		setTimeout(() => {
			if (this.currentVersionCanvas?.nativeElement) {
				this.currentVersionStage.container(this.currentVersionCanvas.nativeElement);
			}

			if (this.newVersionCanvas?.nativeElement) {
				this.newVersionStage.container(this.newVersionCanvas.nativeElement);
			}
			this.canvasResize();
			this.canvasRender();
		});
	}

	ngAfterViewInit() {
		this.currentVersionStage = new Konva.Stage({
			container: this.currentVersionCanvas.nativeElement
		});
		this.currentVersionStage.add(this.planogramLayer);
		this.currentVersionStage.add(this.detalisationLayer);
		this.currentVersionStage.add(this.sizesLayer);
		this.currentVersionStage.add(this.detalisationSizesLayer);
		this.currentVersionStage.add(this.tooltipLayer);
		this.currentVersionStage.add(this.boxLayer);

		this.newVersionStage = new Konva.Stage({
			container: this.newVersionCanvas.nativeElement
		});
		this.newVersionStage.add(this.newPlanogramLayer);
		this.newVersionStage.add(this.newDetalisationLayer);
		this.newVersionStage.add(this.newSizesLayer);
		this.newVersionStage.add(this.newDetalisationSizesLayer);
		this.newVersionStage.add(this.newTooltipLayer);
		this.newVersionStage.add(this.newBoxLayer);

		setTimeout(() => {
			this.updateZoomTargets();
		}, 0);
	}

	private updateZoomTargets(): void {
		const targets: Array<HTMLElement> = [];
		if (this.canvasContainer?.nativeElement) {
			targets.push(this.canvasContainer.nativeElement);
		}
		if (this.newCanvasContainer?.nativeElement) {
			targets.push(this.newCanvasContainer.nativeElement);
		}
		this.zoomTargets = targets;
	}

	canvasResize(): void {
		if (!this.canvasContainer) return;
		const containerWidth: number = this.canvasContainer.nativeElement.clientWidth,
			containerHeight: number = this.canvasContainer.nativeElement.clientHeight;

		let standWidth: number = containerWidth;

		this.stands
			.filter((s: IPlanogram): boolean => s.visible)
			.forEach((stand: any, idx: number): void => {
				const bounds: IPlanogramBounds = Utils.calculatePlanogramBounds(stand.stand);

				let instrumentWidth: number = bounds.width,
					instrumentHeight: number = bounds.height;

				if (instrumentWidth / instrumentHeight > standWidth / containerHeight) {
					stand.scale = Math.floor(((standWidth - canvasOffset * 2) / instrumentWidth) * (this.zoom / 100));
				} else {
					stand.scale = Math.floor(((containerHeight - canvasOffset * 2) / instrumentHeight) * (this.zoom / 100));
				}

				const offsetX: number = bounds.minX < 0 ? Math.abs(bounds.minX) * stand.scale : 0;
				const offsetY: number = (bounds.maxY - stand.stand.Height) * stand.scale;

				stand.width = stand.scale * instrumentWidth;
				stand.height = stand.scale * instrumentHeight;

				stand.beginPoint = { x: offsetX, y: offsetY };
			});

		const width: number = (this.stands.map((s: IPlanogram): number => s.width).reduce((a: number, b: number): number => a + b + 5, 0) + 100) / 2,
			height: number = Math.max(...this.stands.map((s: IPlanogram): number => s.height)) + canvasOffset * 2;

		this.stands
			.filter((s: IPlanogram): boolean => s.visible)
			.forEach((stand: IPlanogram): void => {
				if (containerHeight > height) {
					stand.beginPoint.y += Math.ceil((containerHeight - height) / 2);
				}
				if (stand.height < height - canvasOffset * 2) {
					stand.beginPoint.y += height - canvasOffset * 2 - stand.height;
				}
				stand.beginPoint.y = Math.round(stand.beginPoint.y);
			});

		const scale: number = Utils.min(this.stands.filter((s: IPlanogram): boolean => s.visible).map((s: IPlanogram): number => s.scale)),
			beginPointY: number = Utils.max(this.stands.filter((s: IPlanogram): boolean => s.visible).map((s: IPlanogram): number => s.beginPoint.y));

		this.stands
			.filter((s: IPlanogram): boolean => s.visible)
			.forEach((stand: IPlanogram): void => {
				stand.beginPoint.y = beginPointY;
				stand.scale = scale;
			});

		if (containerWidth > width) {
			const centerOffsetX: number = (containerWidth - width) / 2;
			this.stands
				.filter((s: IPlanogram): boolean => s.visible)
				.forEach((stand: IPlanogram): void => {
					stand.beginPoint.x += centerOffsetX;
				});
		}

		const finalWidth: number = containerWidth > width ? containerWidth - 8 : width;
		const finalHeight: number = containerHeight + 8 >= height ? containerHeight - 8 : height;

		this.currentVersionCanvas.nativeElement.style.width = `${finalWidth}px`;
		this.currentVersionCanvas.nativeElement.style.height = `${finalHeight}px`;
		this.currentVersionStage.width(finalWidth);
		this.currentVersionStage.height(finalHeight);

		this.newVersionCanvas.nativeElement.style.width = `${finalWidth}px`;
		this.newVersionCanvas.nativeElement.style.height = `${finalHeight}px`;
		this.newVersionStage.width(finalWidth);
		this.newVersionStage.height(finalHeight);
	}

	toggleSizesMode() {
		this.sizesMode = !this.sizesMode;

		this.sizesLayer.destroyChildren();
		this.newSizesLayer.destroyChildren();
		this.detalisationSizesLayer.destroyChildren();
		this.newDetalisationSizesLayer.destroyChildren();

		if (!this.sizesMode) {
			this.sizesLayer.draw();
			this.newSizesLayer.draw();
			this.detalisationSizesLayer.draw();
			this.newDetalisationSizesLayer.draw();
			return;
		}
		this.stands.forEach((stand, idx) => {
			this.canvasDrawSizes(stand, idx);
		});

		this.sizesLayer.draw();
		this.newSizesLayer.draw();
		this.detalisationSizesLayer.draw();
		this.newDetalisationSizesLayer.draw();
	}

	canvasRender(stnd = null, elements = null) {
		setTimeout(() => {
			if (this.equipment3DModels?.length) {
				this.equipment3DModels.forEach((model: EquipmentModelComponent) => {
					void model.updateScene();
				});
			}
		});
		if (!this.canvasContainer) return;
		if (stnd && !Array.isArray(stnd)) stnd = [stnd];

		this.canvasClear(stnd, elements);

		try {
			this.sizesLayer.destroyChildren();
			this.newSizesLayer.destroyChildren();
			this.detalisationSizesLayer.destroyChildren();
			this.newDetalisationSizesLayer.destroyChildren();

			this.stands.forEach((stand, idx) => {
				if (!stand.visible) return;

				if (!stnd || stnd.includes(stand)) {
					this.canvasRenderEquipment(stand, elements);
				}
				if (this.sizesMode) {
					this.canvasDrawSizes(stand, idx);
				}
			});
			this.planogramLayer.setZIndex(0);
			this.boxLayer.setZIndex(1);
			this.sizesLayer.setZIndex(2);
			this.detalisationLayer.setZIndex(3);
			this.detalisationSizesLayer.setZIndex(4);
			this.tooltipLayer.setZIndex(5);

			this.planogramLayer.draw();
			this.detalisationLayer.draw();
			this.sizesLayer.draw();
			this.detalisationSizesLayer.draw();
			this.tooltipLayer.draw();
			this.boxLayer.draw();

			this.newPlanogramLayer.setZIndex(0);
			this.newBoxLayer.setZIndex(1);
			this.newSizesLayer.setZIndex(2);
			this.newDetalisationLayer.setZIndex(3);
			this.newDetalisationSizesLayer.setZIndex(4);
			this.newTooltipLayer.setZIndex(5);

			this.newPlanogramLayer.draw();
			this.newDetalisationLayer.draw();
			this.newDetalisationSizesLayer.draw();
			this.newSizesLayer.draw();
			this.newTooltipLayer.draw();
			this.newBoxLayer.draw();
		} catch (e) {
			console.log('ERR IS', e);
			setTimeout(() => {
				this.canvasResize();
				this.canvasRender();
			});
		}
	}

	canvasDrawElement(stand, element: Element) {
		const canvasElement: Konva.Group = this.drawer.createCanvasEquipmentElement(
			{
				element: element,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				active: false,
				flip: this.flipMode.active
			},
			stand.stand
		);

		if (this.transparentBoxesMode) {
			canvasElement.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			canvasElement.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
		}

		if (!this.barriersMode) {
			canvasElement.find('.barrier').forEach((node: Konva.Node) => node.destroy());
		}

		if (stand.detaledElement == element) {
			this.drawer.setEquipmentElementHovered(element);
		} else {
			this.drawer.setEquipmentElementInactive(element);
		}

		canvasElement.off('click mouseenter mouseleave');
		canvasElement.on('click', (event) => {
			if (
				![
					'RectangleBase',
					'TrapezeBase',
					'Shelf',
					'Hook',
					'AngleShelf'
				].includes(element.parent.type)
			)
				return;

			if (!stand.detaledElement || stand.detaledElement != element) {
				stand.detaledElement = element;

				this.activeStand = stand;
				return this.canvasRender(stand);
			}

			this.openDetailedView(stand);
		});

		if (
			[
				'RectangleBase',
				'TrapezeBase',
				'Shelf',
				'Hook',
				'AngleShelf'
			].includes(element.parent.type)
		) {
			canvasElement.on('mouseenter', () => {
				this.drawer.setEquipmentElementHovered(element);
			});
			canvasElement.on('mouseleave', () => {
				if (element == stand.detaledElement) return;
				this.drawer.setEquipmentElementInactive(element);
			});
		}
		// додавання логіки тут

		stand.layerObjects.planogramGroup.add(canvasElement);
		return true;
	}

	canvasDrawSizes(stand: IPlanogram, current: number) {
		if (!stand.detalisation) {
			this[current == 0 ? 'sizesLayer' : 'newSizesLayer'].add(...this.drawer.createPlanogramSizes({
				element: null,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				zoom: this.zoom,
				active: false,
				flip: this.flipMode.active
			}, stand.stand));
		} else {
			this[current == 0 ? 'detalisationSizesLayer' : 'newDetalisationSizesLayer'].add(...this.detailedDrawer.createPlanogramDetailedSizes({
				element: stand.detaledElement,
				beginPoint: stand.beginDetailPoint,
				scale: stand.detalisationScale,
				zoom: this.zoom,
				active: false,
				flip: this.flipMode.active
			}, stand.stand));
		}
	}

	canvasClear(stand = null, elements = null) {
		this.stands.forEach((stnd) => {
			if (!stand || (!elements && stand.includes(stnd))) {
				stnd.layerObjects.planogramGroup.destroyChildren();
				stnd.layerObjects.detalisationGroup.destroyChildren();
				stnd.layerObjects.tooltipGroup.destroyChildren();
				stnd.layerObjects.boxGroup.destroyChildren();

				Object.keys(stnd.layerObjects).forEach((key) => {
					if (stnd.layerObjects[key].planogramGroup) {
						if (!stnd.detalisation) stnd.layerObjects[key].planogramGroup.destroyChildren();
						stnd.layerObjects[key].detalisationGroup.destroyChildren();
						stnd.layerObjects[key].tooltipGroup.destroyChildren();
						stnd.layerObjects[key].boxGroup.destroyChildren();
					}
				});
				return;
			}
			if (stand.includes(stnd) && elements) {
				Object.keys(stnd.layerObjects).forEach((key) => {
					if (!stnd.layerObjects[key].planogramGroup || !elements.includes(+key)) return;
					stnd.layerObjects[key].planogramGroup.destroyChildren();
					stnd.layerObjects[key].detalisationGroup.destroyChildren();
					stnd.layerObjects[key].tooltipGroup.destroyChildren();
					stnd.layerObjects[key].boxGroup.destroyChildren();
				});
			}
		});
	}

	toggleArticleMode() {
		if (!this.equipmentMode) this.articleMode = !this.articleMode;

		this.canvasRender();
	}

	toggleSequenceNumberMode() {
		if (!this.equipmentMode) this.sequenceNumberMode = !this.sequenceNumberMode;

		this.canvasRender();
	}

	toggleFlip(): void {
		this.flipMode.toggle();
		this.stands[0].flip = this.flipMode.active;
		void this.canvasRender();
	}

	toggleTransparentBoxesMode() {
		this.transparentBoxesMode = !this.transparentBoxesMode;
		if (this.transparentBoxesMode) {
			this.mixPanelService.trackPage(MixpanelEvents.TransparentBoxes, { page: 'execution' });
		}

		this.canvasRender();
	}

	toggleBarriersMode(): void {
		this.barriersMode = !this.barriersMode;
		this.canvasRender();
	}

	changePOSMaterialsMode(): void {
		this.POSMaterialsMode = !this.POSMaterialsMode;
		this.canvasRender();
	}

	export(): void {
		const filename: string = this.stands[0].name.substring(0, 150);

		const elementId: string = this.stands[this.complectionView === 'current' ? 0 : 1].detaledElement?.id;

		const requestData: IStandEquipmentRequest = {
			id: this.stands[this.complectionView === 'current' ? 0 : 1].id,
			name: this.dateFormatService.buildExportFileName(filename),
			planogram_id: this.planId,
			article_photo_mode: this.articleMode,
			sequence_number_mode: this.sequenceNumberMode,
			transparent_boxes: this.transparentBoxesMode,
			show_barriers: this.barriersMode,
			show_pos_materials: this.POSMaterialsMode,
			view_mode: this.stands[1].detalisation ? 'top' : 'direct',
			element_id: elementId,
			size_mode: this.sizesMode,
			block_mode: true,
			show_prices: false,
			show_promo_products: false,
			show_new_products: false,
			show_articles: false,
			show_data: this.standData,
			search: this.complectionView ? this.planogramCompletionService.requestParams.getParam('search') : '',
			search_fields: (this.planogramCompletionService.requestParams.getParam('search_fields') || []).join(',')
		};

		this.dialog
			.open<EquipmentsDownloadModalComponent, IInputEquipmentsModalData>(EquipmentsDownloadModalComponent, {
				width: '860px',
				height: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					title: 'STAND_EQUIPMENTS.DOWNLOAD_PLANOGRAM_COMPLETATION_TITLE',
					downloadMethod: this.excelService.exportStandEquipment.bind(this.excelService),
                    pipeMethod: finalize(() => this.isExporting.set(false)),
					params: requestData,
					showPlanogramViewSelector: true,
					hasMasterPositions: this.stands[this.complectionView === 'current' ? 0 : 1].stand.products.some((pg) => pg.product.is_master_position),
					previewStand: this.stands[this.complectionView === 'current' ? 0 : 1].stand,
					previewPhotos: !this.articleMode,
					previewPOSMaterials: this.POSMaterialsMode ? this.stands[this.complectionView === 'current' ? 0 : 1].stand.POSMaterials || [] : [],
					additionalParams: STAND_COMPLETION_ADDITIONAL_PARAMS
				}
			})
			.afterClosed()
			.subscribe((result: boolean) => {
				if (!result) return;
                this.isExporting.set(true);
				this.mixPanelService.trackPage(MixpanelEvents.DownloadPlanogramConfiguration, result);
				this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.DownloadCompletion);
			});
	}

	canvasBlurCurrentVersionCanvas(stand) {
		const blur = new Konva.Rect({
			x: stand.beginPoint.x + canvasOffset,
			y: 0,
			height: this.canvasContainer.nativeElement.clientHeight,
			width: stand.width,
			fill: konvaSettings.blurColor,
			stroke: konvaSettings.blurColor
		});
		stand.layerObjects.detalisationGroup.add(blur);
		return true;
	}

	canvasDrawProductGroup(stand, productGroup: ProductGroup) {
		let dataObject =
			(this.standData[stand.id] || {})[productGroup.getIdHash()] ||
			(this.standData[stand.id] || {})[String(productGroup.getIdHash())] || {};

		if (
			this.stands.every((s) => !!s.stand.products.length) &&
			Object.keys(dataObject).length &&
			!Utils.equalObjects(
				Object.assign({}, productGroup.begin, {
					faces: productGroup.FacesQty,
					depth: productGroup.Depth,
					height: productGroup.Height
				}),
				Object.assign(
					{},
					this.errorStands[stand.id][String(productGroup.getIdHash())].begin,
					{
						faces:
						this.errorStands[stand.id][String(productGroup.getIdHash())]
							.FacesQty,
						depth:
						this.errorStands[stand.id][String(productGroup.getIdHash())]
							.Depth,
						height:
						this.errorStands[stand.id][String(productGroup.getIdHash())]
							.Height
					}
				),
				true
			)
		) {
			dataObject = null;
		}

		const canvasElement: Konva.Group = this.drawer.createCanvasProduct({
			productGroup: productGroup,
			stand: stand.stand,
			scale: stand.scale,
			beginPoint: stand.beginPoint,
			articleMode: this.articleMode,
			flipMode: this.flipMode.active,
			sequenceNumberMode: this.sequenceNumberMode,
			dataObject: dataObject,
			callback: () => this.planogramLayer.batchDraw()
		});

		canvasElement.off('mouseenter mouseleave');

		stand.layerObjects.planogramGroup.add(canvasElement);

		if (stand.detalisation) return;

		const keys = Object.keys(dataObject || {})
			.filter(
				(k) =>
					!['color', 'product_id', 'promo_product', 'new_product'].includes(k)
			)
			.map((k) => k.toUpperCase());

		this.translate
			.get(['COMMON.PLANOGRAM.SEQUENCE_NUMBER', 'FACES_QTY', 'MASTER_POSITION.TITLE', 'PLACEMENT_DEPTH', ...keys])
			.subscribe((trans) => {
				let tooltip = productGroup.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);

				tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${productGroup.number}`;
				tooltip += `\n${trans['FACES_QTY']}: ${productGroup.FacesQty}`;
				tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${productGroup.displayPlacementDepth}`;

				if (dataObject) {
					keys.forEach((key) => {
						tooltip += `\n${trans[key.toUpperCase()]}: ${
							dataObject[key.toLowerCase()]
						}`;
					});
				}

				const element: ElementRef = this.stands.indexOf(stand) ? this.newVersionCanvas : this.currentVersionCanvas,
					tooltipLabel: Konva.Label = this.drawer.createCanvasProductTooltip(productGroup, tooltip, element.nativeElement);

				stand.layerObjects.detalisationGroup.add(tooltipLabel);
			});
		canvasElement.on('mouseenter', () => {
			this.drawer.getCanvasProductTooltip(productGroup).show();
			this.detalisationLayer.draw();
			this.newDetalisationLayer.draw();
		});
		canvasElement.on('mouseleave', () => {
			this.drawer.getCanvasProductTooltip(productGroup).hide();
			this.detalisationLayer.draw();
			this.newDetalisationLayer.draw();
		});
	}

	canvasRenderEquipment(stand, elements = null) {
		if (!stand.detalisation) {
			stand.stand.orderedElements
				.filter((element: Element) => element.constructor.name != 'HoleElement')
				.forEach((element: Element, idx: number) => {
					if (!elements || elements.includes(idx)) this.canvasDrawElement(stand, element);
				});

			this.drawBlocks(stand);
			if (this.POSMaterialsMode) this.drawPosMaterials(stand);

			stand.stand.blocks.forEach((block: Block) => this.drawer.getCanvasBlock(block).moveToTop());

			stand.stand.products
				.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max)
				.forEach((pg: ProductGroup) => {
					const idx = stand.stand.allElements.indexOf(pg.element);
					if (!elements || elements.includes(idx)) this.canvasDrawProductGroup(stand, pg);
				});

			this.drawer.setCorrectPlanogramOrder(stand.stand, this.flipMode.active);

			stand.stand.blocks.forEach((block: Block) => {
				if (block.colorAlpha == 1) this.drawer.getCanvasBlock(block).moveToTop();
			});
		}

		if (stand.detalisation) {
			this.canvasBlurCurrentVersionCanvas(stand);
			this.canvasDetailResize(stand);

			const detailedElements: Array<Element> = [stand.detaledElement];

			if (stand.detaledElement.depend?.parent.is(...BACK_WALLS_LIST)) {
				if (stand.detaledElement.depend.getBoundingY().max > stand.detaledElement.getBoundingY().max) {
					detailedElements.push(stand.detaledElement.depend);
				}
			}

			detailedElements.push(
				...stand.stand.orderedElements.filter((el: Element) => el.depend == stand.detaledElement),
				...stand.stand.getVerticalIntersectedElements(stand.detaledElement)
			);

			const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory.createBehavior(stand, stand.detaledElement, stand.detalisationScale, stand.beginDetailPoint);
			const offset: Point = behavior.getOffset();
			if (stand.detaledElement.parent.is(EquipmentElements.Hook)) offset.y -= 0.025;

			detailedElements.forEach((el: Element) => this.drawDetailedElement(stand, el, offset));

			const detailedProducts: Array<ProductGroup> = stand.stand.products
				.filter((pg: ProductGroup) => detailedElements.includes(pg.element));

			detailedProducts.forEach((pg: ProductGroup) => this.drawDetailedProduct(stand, pg, offset));

			detailedProducts
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
				.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z)
				.forEach((pg: ProductGroup) => this.detailedDrawer.getCanvasDetailedProduct(pg).moveToTop());
		}

		this.planogramLayer.draw();
		this.newPlanogramLayer.draw();
	}

	drawDetailedProduct(stand, product: ProductGroup, normalize) {
		const dataObject =
				(this.standData[stand.id] || {})[product.getIdHash()] ||
				(this.standData[stand.id] || {})[String(product.getIdHash())] ||
				{},
			canvasElement: Konva.Group = this.detailedDrawer.createCanvasDetailedProduct({
				productGroup: product,
				stand: stand.stand,
				scale: stand.detalisationScale,
				beginPoint: stand.beginDetailPoint,
				normalize: normalize,
				articleMode: this.articleMode,
				sequenceNumberMode: this.sequenceNumberMode,
				dataObject: dataObject,
				callback: () => {
					this.detalisationLayer.draw();
					this.newDetalisationLayer.draw();
				}
			});

		canvasElement.off('mouseenter mouseleave');

		let size_metric = 'PLACEMENT_DEPTH';

		if (
			['BoxElement', 'PalletElement'].includes(product.element.constructor.name)
		) {
			size_metric = 'PLACEMENT_HEIGHT';
		}

		this.translate.get(['COMMON.PLANOGRAM.SEQUENCE_NUMBER', 'MASTER_POSITION.TITLE', 'FACES_QTY', size_metric]).subscribe((trans) => {
			let tooltip = product.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);
			tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${product.number}`;
			tooltip += `\n${trans['FACES_QTY']}: ${product.FacesQty}`;
			if (size_metric == 'PLACEMENT_DEPTH')
				tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${product.displayPlacementDepth}`;

			if (size_metric == 'PLACEMENT_HEIGHT')
				tooltip += `\n${trans['PLACEMENT_HEIGHT']}: ${product.displayPlacementHeight}`;

			const element: ElementRef = this.stands.indexOf(stand) ? this.newVersionCanvas : this.currentVersionCanvas,
				tooltipLabel: Konva.Label = this.detailedDrawer.createCanvasDetailedProductTooltip(product, tooltip, element.nativeElement);

			stand.layerObjects.tooltipGroup.add(tooltipLabel);
		});
		canvasElement.on('mouseenter', () => {
			this.detailedDrawer.getCanvasDetailedProductTooltip(product).show();
			this.tooltipLayer.draw();
			this.newTooltipLayer.draw();
		});
		canvasElement.on('mouseleave', () => {
			this.detailedDrawer.getCanvasDetailedProductTooltip(product).hide();
			this.tooltipLayer.draw();
			this.newTooltipLayer.draw();
		});

		stand.layerObjects.detalisationGroup.add(canvasElement);
	}

	canvasDetailResize(stand) {
		if (!stand.detalisation || !stand.detaledElement) {
			stand.beginDetailPoint = { x: 0, y: 0 };
			return;
		}

		const containerWidth: number = this.canvasContainer.nativeElement.clientWidth,
			offsetContainerWidth: number = containerWidth - konvaSettings.canvasOffset * 2,
			containerHeight: number = this.canvasContainer.nativeElement.clientHeight,
			offsetContainerHeight: number = containerHeight - konvaSettings.canvasOffset * 6;

		let instrumentWidth: number, instrumentHeight: number;

		switch (stand.detaledElement.parent.type) {
			case 'Hook': {
				const minHookHeight: number = 0.5;
				const hangerHeight: number = stand.detaledElement.hangerDistance;
				const maxProductHeight: number = Math.max(
					...stand.detaledElement.products.flat().map((pg: ProductGroup) => pg.Height + hangerHeight),
					minHookHeight
				);
				instrumentWidth = stand.detaledElement.Depth;
				instrumentHeight = stand.detaledElement.Height + maxProductHeight + 0.05;
				break;
			}
			default: {
				instrumentWidth = stand.detaledElement.Width;
				instrumentHeight = stand.detaledElement.Depth;
			}
		}

		if (offsetContainerWidth / offsetContainerHeight < instrumentWidth / instrumentHeight) {
			stand.detalisationScale = Math.floor(offsetContainerWidth / instrumentWidth);
		} else {
			stand.detalisationScale = Math.floor(offsetContainerHeight / instrumentHeight);
		}

		const width: number = stand.detalisationScale * instrumentWidth + konvaSettings.canvasOffset * 2,
			height: number = stand.detalisationScale * instrumentHeight + konvaSettings.canvasOffset * 6;

		stand.beginDetailPoint = { x: 0, y: 0 };

		if (containerWidth > width) stand.beginDetailPoint.x = Math.ceil((containerWidth - width) / 2);
		if (containerHeight > height) stand.beginDetailPoint.y = Math.ceil((containerHeight - height) / 2);
	}

	drawDetailedElement(stand, element: Element, normalize) {
		const canvasElement: Konva.Group = this.detailedDrawer.createCanvasDetailedEquipmentElement({
			element: element,
			beginPoint: stand.beginDetailPoint,
			scale: stand.detalisationScale,
			normalize: normalize,
			active: false
		}, stand.stand);

		stand.layerObjects.detalisationGroup.add(canvasElement);
	}

	setEquipmentMode() {
		this.equipmentMode = !this.equipmentMode;
		this.complectionView = 'execution';
		if (this.equipmentMode) {
			// TODO equipment
			// if(this.filterType == "square") this.showSquareAnalys();
			// if(this.filterType == "default") this.closeFilters();
			// if(this.filterType == "distribution") this.showSquareAnalys();
		}
	}

	drawBlocks(stand) {
		stand.stand.blocks.forEach((b: Block) => {
			const canvasElement: Konva.Group = this.drawer.createCanvasBlock({
					block: b,
					beginPoint: stand.beginPoint,
					scale: stand.scale,
					active: false,
					selected: false,
					draggable: false,
					zoom: this.zoom,
					flip: this.flipMode.active
				}),
				blockName: Konva.Text = this.drawer.getCanvasBlockName(b),
				blockDescriptionIcon: Konva.Image = this.drawer.getCanvasBlockDescriptionIcon(b),
				element: ElementRef = this.stands.indexOf(stand) ? this.newVersionCanvas : this.currentVersionCanvas;

			if (this.drawer.getCanvasBlockTooltipNecessity(b)) {
				const tooltip: Konva.Label = this.drawer.createCanvasBlockTooltip(b, b.name, element.nativeElement);

				stand.layerObjects.tooltipGroup.add(tooltip);
				blockName.listening(true);

				blockName.on('mouseenter', () => {
					tooltip.show();
					this.tooltipLayer.draw();
				});

				blockName.on('mouseleave', () => {
					tooltip.hide();
					this.tooltipLayer.draw();
				});
			}

			if (b.description) {
				const descriptionTooltip = this.drawer.createCanvasBlockDescriptionTooltip(
					b,
					b.description,
					element.nativeElement
				);
				stand.layerObjects.tooltipGroup.add(descriptionTooltip);
				blockDescriptionIcon.listening(true);

				blockDescriptionIcon.on('mouseenter', () => {
					descriptionTooltip.show();
					this.tooltipLayer.draw();
				});

				blockDescriptionIcon.on('mouseleave', () => {
					descriptionTooltip.hide();
					this.tooltipLayer.draw();
				});
			}

			if (b.products.length) {
				this.drawer.getCanvasBlockBody(b).hide();
			}

			stand.layerObjects.boxGroup.add(canvasElement);
		});
	}

	drawPosMaterials(stand: IPlanogram): void {
		const element: ElementRef = this.stands.indexOf(stand) ? this.newVersionCanvas : this.currentVersionCanvas;

		for (let material of stand.stand.POSMaterials) {
			const canvasLabel: Konva.Group = this.drawer.createCanvasPOSMaterial({
				material: material,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				zoom: this.zoom,
				draggable: false,
				active: false,
				tooltipGroup: stand.layerObjects.tooltipGroup,
				canvasElement: element.nativeElement
			});

			stand.layerObjects.boxGroup.add(canvasLabel);
		}
	}

	// застосування нового масштабу
	async zoomApply(value: number) {
		this.zoom = value;
		this.canvasResize();
		this.canvasRender();
	}

	changeIndex(array, fromIndex, toIndex) {
		const element = array[fromIndex];
		array.splice(fromIndex, 1);
		array.splice(toIndex, 0, element);
		return array;
	}

	openLayout() {
		this.layoutOpen = !this.layoutOpen;
	}

	redirectStand() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'view', this.selectedStand]),
			{
				relativeTo: this.route,
				queryParams: { planogram: this.route.snapshot.queryParams.planogram },
				queryParamsHandling: 'merge'
			}
		);
	}

	redirectPlanogram() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'view', this.route.snapshot.params.id]),
			{ queryParams: { planogram: this.selectedPlanogram } }
		);
	}

	calculateZoom(product: ProductGroup) {
		let canvasTopPoint =
			this.currentVersionStage.getAttr('height') / this.stands[0].scale;
		do {
			this.zoom = this.zoom - 20;
			this.zoomApply(this.zoom);
			canvasTopPoint =
				this.currentVersionStage.getAttr('height') / this.stands[0].scale;
		} while (
			canvasTopPoint - product.begin.y - product.product.height < 0 &&
			this.zoom >= 20
			);
	}

	@HostListener('window:focus') onTabOpen(): void {
		if (this.currentVersionStage) this.currentVersionStage.batchDraw();
		if (this.newVersionStage) this.newVersionStage.batchDraw();
	}

	@HostListener('window:resize', ['$event']) windowResize() {
		// перемальовка канви після кожної зміни розміру екрана
		this.canvasResize();
		this.canvasRender();
	}

	ngOnDestroy() {
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
		this.socketSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		this.socketService.deleteSubscription(SocketGroup.StandConversation, { stand_id: Number(this.standId) });
		this.currentVersionStage.destroy();
		this.newVersionStage.destroy();
	}
}
